import { useState, type ReactNode } from 'react';

// Subtle illuminated info mark: a small italic serif "i" that sits
// gracefully alongside display type. Reveals a tooltip on hover/focus.
// Idle footprint is ~14px wide and visually quiet; only the letter.
export function InfoHint({
  children,
  side = 'right',
  className = '',
}: {
  children: ReactNode;
  /** Which side the bubble prefers to align to. */
  side?: 'left' | 'right';
  className?: string;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const visible = hovered || focused;

  const bubbleAlign = side === 'left' ? 'right-0' : 'left-0';

  return (
    <span
      className={`relative inline-block align-baseline ml-1.5 ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        aria-label="More info"
        aria-expanded={visible}
        onClick={() => setFocused((f) => !f)}
        onBlur={() => setFocused(false)}
        className="group relative inline-flex h-[14px] w-[14px] items-center justify-center
                   text-gold-400/55 transition-colors hover:text-gold-200
                   focus:outline-none focus:text-gold-200"
      >
        {/* Thin gold ring — very light by default, crisp on hover */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-full
                     border border-gold-400/25 transition-[border-color,background-color,transform]
                     duration-200 group-hover:border-gold-300/80
                     group-hover:bg-gold-400/12 group-focus:border-gold-300/80
                     group-focus:bg-gold-400/12 group-hover:scale-110 group-focus:scale-110"
        />
        {/* Serif italic 'i' */}
        <span
          aria-hidden="true"
          className="relative font-serif italic leading-none"
          style={{ fontSize: '11px', transform: 'translateY(-0.5px)' }}
        >
          i
        </span>
      </button>
      {visible && (
        <span
          role="tooltip"
          className={`absolute ${bubbleAlign} top-full mt-2 z-50 w-[min(320px,80vw)]
                     rounded-md border border-gold-400/40 bg-ink-800/95 backdrop-blur-sm
                     px-3 py-2 text-xs leading-relaxed text-parchment-100/85
                     shadow-soft font-sans text-left`}
          style={{ textTransform: 'none', letterSpacing: 'normal' }}
        >
          {children}
        </span>
      )}
    </span>
  );
}
